import { useState, useEffect, useCallback, useRef } from 'react';

const SW_PATH = '/firebase-messaging-sw.js';
const UPDATE_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes (fallback)
const DISMISSED_VERSION_KEY = 'dismissed_sw_version';

/**
 * Hook to check for new application versions using Service Worker
 * Also monitors online/offline status
 *
 * How it works:
 * 1. Registers the Service Worker
 * 2. Browser automatically checks for SW updates on navigation and periodically
 * 3. When SW file changes (new build), browser triggers 'updatefound' event
 * 4. We notify the user and let them choose when to refresh
 *
 * Benefits over polling:
 * - Zero network requests for version checking
 * - Browser handles update detection automatically
 * - Works even when tab is in background
 *
 * @returns {Object} { hasNewVersion, currentVersion, newVersion, refreshApp, dismissUpdate, isOffline }
 */
// Helper to get version from a service worker via MessageChannel
const getVersionFromSW = (sw) =>
    new Promise((resolve) => {
        const messageChannel = new MessageChannel();
        const timeout = setTimeout(() => resolve(null), 1000);
        messageChannel.port1.onmessage = (event) => {
            clearTimeout(timeout);
            resolve(event.data?.version || null);
        };
        try {
            sw.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);
        } catch {
            clearTimeout(timeout);
            resolve(null);
        }
    });

export default function useVersionCheck() {
    const [hasNewVersion, setHasNewVersion] = useState(false);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [newVersion, setNewVersion] = useState(null);
    const [registration, setRegistration] = useState(null);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const waitingWorkerRef = useRef(null);
    const waitingVersionRef = useRef(null);

    // Monitor online/offline status
    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Register Service Worker and set up update detection
    useEffect(() => {
        if (!('serviceWorker' in navigator)) {
            console.warn('[VersionCheck] Service Workers not supported');
            return undefined;
        }

        let mounted = true;

        const registerSW = async () => {
            try {
                const reg = await navigator.serviceWorker.register(SW_PATH);

                if (!mounted) return;

                setRegistration(reg);

                // Helper to check if version was dismissed
                const isDismissed = (version) => {
                    if (!version) return false;
                    const dismissed = localStorage.getItem(DISMISSED_VERSION_KEY);
                    return dismissed === version;
                };

                // Helper to notify about new version (only if not dismissed)
                const notifyNewVersion = async (worker) => {
                    const version = await getVersionFromSW(worker);
                    if (version && !isDismissed(version)) {
                        waitingWorkerRef.current = worker;
                        waitingVersionRef.current = version;
                        setNewVersion(version);
                        setHasNewVersion(true);
                    }
                };

                // Check if there's already a waiting worker (update available)
                if (reg.waiting) {
                    notifyNewVersion(reg.waiting);
                }

                // Listen for new updates
                reg.addEventListener('updatefound', () => {
                    const newWorker = reg.installing;

                    if (!newWorker) return;

                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            // New version installed and waiting to activate
                            notifyNewVersion(newWorker);
                        }
                    });
                });

                // Get current version from active SW
                if (reg.active) {
                    const version = await getVersionFromSW(reg.active);
                    if (version) {
                        setCurrentVersion(version);
                    }
                }
            } catch (err) {
                console.error('[VersionCheck] SW registration failed:', err);
            }
        };

        registerSW();

        // Listen for SW controller change (when new SW takes over)
        const handleControllerChange = () => {
            // New SW is now controlling the page, reload to get new content
            window.location.reload();
        };

        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

        return () => {
            mounted = false;
            navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        };
    }, []);

    // Periodic check for updates (fallback, browser does this automatically but we can be proactive)
    useEffect(() => {
        if (!registration) return undefined;

        const checkForUpdate = () => {
            registration.update().catch(() => {
                // Ignore update check errors
            });
        };

        // Check on visibility change (when user returns to tab)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkForUpdate();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Also check periodically as fallback
        const intervalId = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(intervalId);
        };
    }, [registration]);

    // Refresh the app - tell waiting SW to take over
    const refreshApp = useCallback(() => {
        const waitingWorker = waitingWorkerRef.current;

        // Clear dismissed version since user is updating
        localStorage.removeItem(DISMISSED_VERSION_KEY);

        if (waitingWorker) {
            // Tell the waiting SW to skip waiting and activate
            waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        }

        // Clear caches
        if ('caches' in window) {
            caches.keys().then((names) => {
                names.forEach((name) => {
                    caches.delete(name);
                });
            });
        }

        // The controllerchange event will trigger a reload
        // But if it doesn't happen quickly, force reload
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    }, []);

    // Dismiss the update notification - stores version to prevent re-prompting
    const dismissUpdate = useCallback(() => {
        // Store dismissed version so we don't prompt again for this version
        if (waitingVersionRef.current) {
            localStorage.setItem(DISMISSED_VERSION_KEY, waitingVersionRef.current);
        }
        setHasNewVersion(false);
        setNewVersion(null);
    }, []);

    return {
        hasNewVersion,
        currentVersion,
        newVersion,
        refreshApp,
        dismissUpdate,
        isOffline,
    };
}
